import { Checkbox, FormControlLabel } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import OutlineCard from "../../../../../components/OutlineCard";
import Text from "../../../../../components/Text";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useCreateTwilioNumber } from "../../../../../queries/practice-setting/configuration/use-create-twilio-number";
import { getXtenantId, showToastOnApiCall } from "../../../../../utils/utils";
import DeleteIcon from "../../../../../components/app/DeleteIcon";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../components/app/DeleteModal";

const callerIdValidationSchema = Yup.object().shape({
  callerId: Yup.string()
    .nullable()
    .when([], {
      is: () => true,
      then: (schema) =>
        schema
          .test(
            "caller-id-validation",
            "Caller ID must contain numeric digits only.",
            (value) => !value || /^\d+$/.test(value)
          )
          .test(
            "caller-id-length",
            "Caller ID must be 10 digits.",
            (value) => !value || value.length === 10
          ),
    }),
});
interface CallerIdVerificationFormProps {
  prefillData?: any;
  onSetCallerId: () => void;
  providerDetails?: any;
  configurationDetails?: any;
}
interface CallerIDFormValues {
  callerId: string;
  verifyId: boolean;
}

const CALLER_ID_INITIAL_VALUES: CallerIDFormValues = {
  callerId: "",
  verifyId: false,
};

const CallerIdVerificationForm = ({
  onSetCallerId,
  providerDetails,
  configurationDetails,
}: CallerIdVerificationFormProps) => {
  const params = useParams();
  const showSnackbar = useSnackbar();
  const deleteCallerIdDisc = useDeleteDisClosure();
  const { mutateAsync: addCallerId } = useCreateTwilioNumber();

  const callerIdFormik = useFormik<CallerIDFormValues>({
    initialValues: CALLER_ID_INITIAL_VALUES,
    validationSchema: callerIdValidationSchema,
    onSubmit: (values) => {
      if (!configurationDetails?.data?.caller_id) {
        if (!values.verifyId) {
          showSnackbar({
            message:
              "Please ensure Caller ID and Email ID are entered before verification.",
            type: "error",
          });
          return;
        }
      }

      const payload = {
        twilioCallerId: {
          caller_id: values.callerId,
        },
        providerGroupId: (providerDetails?.data as any)?.uuid,
        xTENANTID: getXtenantId() ?? params?.providerGroupId ?? undefined,
      };
      showToastOnApiCall({
        apiCall: addCallerId(payload),
        showSnackbar,
        onSave: () => {
          onSetCallerId();
          callerIdFormik.resetForm();
        },
      });
    },
  });

  return (
    <OutlineCard>
      <Flex flexDirection="column" width="100%">
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom="20px"
        >
          <Text fontSize="16px" fontWeight="600">
            Caller ID Verification
          </Text>
        </Flex>

        <Flex flexDirection="column" gap="16px">
          <Flex alignItems="center" gap={1}>
            <Text fontSize="14px" color="text.secondary">
              Caller ID:{" "}
              {configurationDetails?.data?.caller_id
                ? `${configurationDetails?.data?.caller_id} ${
                    configurationDetails?.data?.isVerified ? "(Verified)" : ""
                  }`
                : "Not Verified"}
            </Text>
            <DeleteIcon
              disabled={!configurationDetails?.data?.caller_id}
              onClick={() => deleteCallerIdDisc.open()}
            />
            {/* <IconButton
              disabled={!configurationDetails?.data?.caller_id}
              onClick={() => {
                callerIdFormik.setFieldValue("callerId", null);
                callerIdFormik.submitForm();
              }}
            >
              <ClearIcon
                fontSize="small"
                color={
                  !configurationDetails?.data?.caller_id ? "disabled" : "error"
                }
              />
            </IconButton> */}
            {/* <DeleteIcon
              disabled={configurationDetails?.data?.caller_id?.length === 0}
              onClick={() => setClearCallerId(true)}
            /> */}
          </Flex>

          <Input
            error={
              callerIdFormik.touched.callerId && callerIdFormik.errors.callerId
                ? callerIdFormik.errors.callerId
                : undefined
            }
            textFieldProps={{
              placeholder: "Enter Caller ID",
              name: "callerId",
              value: callerIdFormik.values.callerId,
              onChange: callerIdFormik.handleChange,
              onBlur: callerIdFormik.handleBlur,
              disabled:
                // !clearCallerId ||
                configurationDetails?.data?.caller_id?.length > 0,
            }}
          />

          <Flex alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={callerIdFormik.values.verifyId}
                  disabled={
                    // !clearCallerId ||
                    configurationDetails?.data?.caller_id?.length > 0
                  }
                  onChange={(e) =>
                    callerIdFormik.setFieldValue("verifyId", e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Verify ID"
            />
            <StyledButtonV2
              variant="contained"
              color="primary"
              label="Create Caller ID"
              onClick={() => callerIdFormik.handleSubmit()}
              disabled={
                callerIdFormik.values?.callerId?.trim()?.length === 0 ||
                !!callerIdFormik.errors.callerId ||
                // !clearCallerId ||
                configurationDetails?.data?.caller_id?.length > 0
              }
            />
          </Flex>
        </Flex>
      </Flex>
      <DeleteModal
        w="380px"
        open={deleteCallerIdDisc.opened}
        onClose={deleteCallerIdDisc.close}
        onConfirmation={() => {
          callerIdFormik.setFieldValue("callerId", null);
          return callerIdFormik.submitForm();
        }}
        content="Are you sure you want to delete this verified Caller ID?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </OutlineCard>
  );
};

export default CallerIdVerificationForm;
